import { useEffect, useLayoutEffect, useRef } from 'react';
import { useStickToBottomContext } from 'use-stick-to-bottom';

const TOP_OFFSET_PX = 16;

type Props = {
  lastUserMessageId: string | undefined;
  justSent: boolean;
};

/**
 * Pins a freshly sent message to the top of the viewport, the way the answer
 * appears to unroll beneath it. Renders nothing — it only drives the scroller.
 */
export const AnchorToSentMessage = ({ lastUserMessageId, justSent }: Props) => {
  const { scrollRef, contentRef, stopScroll } = useStickToBottomContext();
  const anchoredId = useRef<string | undefined>(undefined);
  const basePadding = useRef<number | undefined>(undefined);
  const observer = useRef<ResizeObserver | undefined>(undefined);

  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    const content = contentRef.current;
    if (
      !justSent ||
      !lastUserMessageId ||
      anchoredId.current === lastUserMessageId ||
      !scroller ||
      !content
    )
      return;
    anchoredId.current = lastUserMessageId;
    const message = content.querySelector<HTMLElement>(
      `[data-message-id="${CSS.escape(lastUserMessageId)}"]`
    );
    if (!message) return;
    basePadding.current ??=
      parseFloat(getComputedStyle(content).paddingBottom) || 0;

    const padToKeepAnchorReachable = () => {
      const currentPadding =
        parseFloat(getComputedStyle(content).paddingBottom) || 0;
      const turnHeight =
        content.getBoundingClientRect().bottom -
        currentPadding -
        message.getBoundingClientRect().top;
      const padding = Math.max(
        basePadding.current ?? 0,
        scroller.clientHeight - TOP_OFFSET_PX - turnHeight
      );
      content.style.paddingBottom = `${padding}px`;
    };

    padToKeepAnchorReachable();
    stopScroll();
    scroller.scrollTo({
      top:
        message.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop -
        TOP_OFFSET_PX,
      behavior: 'instant',
    });

    observer.current?.disconnect();
    observer.current = new ResizeObserver(padToKeepAnchorReachable);
    observer.current.observe(content);
  }, [justSent, lastUserMessageId, scrollRef, contentRef, stopScroll]);

  // unmount-only: the effect above re-runs once the reply arrives, and the observer must outlive that
  useEffect(() => () => observer.current?.disconnect(), []);

  return null;
};
